import { useState } from 'react';
import { Truck, ChevronDown, ChevronUp } from 'lucide-react';

/**
 * Renders the ADDITIONAL trailers (Trailer 2, 3, 4...) of a rig allocation.
 * The PRIMARY trailer is already rendered by the parent (allocation.equipment_id),
 * so this component only handles allocation.additional_trailer_equipment_ids.
 *
 * Display rule — optimised for the common 2-trailer case, flexible for 3+:
 *   • 1 additional trailer  -> shown inline (2 trailers visible total)
 *   • 2+ additional         -> first shown inline + a "+N more" badge that
 *                              expands on tap and reveals the full list on hover (title).
 *
 * variant: 'list' | 'week' | 'board'
 *   - 'list' shows a small truck icon + full "Type / AssetId" label
 *   - 'week' / 'board' are compact (asset id / name only)
 */
export const RigTrailers = ({ trailerIds = [], getEquipmentInfo, variant = 'list', jobId }) => {
  const [open, setOpen] = useState(false);

  const trailers = (trailerIds || [])
    .filter(Boolean)
    .map((id) => getEquipmentInfo(id))
    .filter(Boolean);

  if (trailers.length === 0) return null;

  const full = (t) => `${t.equipment_type || t.name || 'Trailer'} / ${t.asset_id || ''}`.replace(/\s*\/\s*$/, '');
  const short = (t) => t.asset_id || t.name || 'Trailer';
  const labelFor = variant === 'list' ? full : short;

  const first = trailers[0];
  const rest = trailers.slice(1);
  // Tooltip lists every additional trailer (Trailer 2 onward) for hover discovery
  const hoverTitle = trailers.map((t, i) => `Trailer ${i + 2}: ${full(t)}`).join('\n');

  const lineCls = 'flex items-center gap-1 truncate text-muted-foreground';
  const showIcon = variant === 'list';

  return (
    <div className="space-y-0.5" data-testid={`rig-trailers-${jobId}`}>
      {/* First additional trailer (Trailer 2) — always inline */}
      <div className={lineCls}>
        {showIcon && <Truck className="h-3 w-3 text-amber-400/70 shrink-0" />}
        <span className="truncate">{labelFor(first)}</span>
      </div>

      {/* Trailer 3+ — count badge that expands on tap / reveals on hover */}
      {rest.length > 0 && (
        <>
          <button
            type="button"
            title={hoverTitle}
            data-testid={`rig-trailers-more-${jobId}`}
            onClick={(e) => {
              e.stopPropagation();
              setOpen((o) => !o);
            }}
            className="inline-flex items-center gap-0.5 rounded-full bg-amber-500/15 px-1.5 py-0.5 text-[10px] font-semibold text-amber-300 hover:bg-amber-500/25 transition-colors"
          >
            +{rest.length} more
            {open ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
          </button>

          {open && (
            <div className="space-y-0.5 pl-1" data-testid={`rig-trailers-expanded-${jobId}`}>
              {rest.map((t, i) => (
                <div key={t.id || i} className={lineCls}>
                  {showIcon && <Truck className="h-3 w-3 text-amber-400/70 shrink-0" />}
                  <span className="truncate text-[11px]">{labelFor(t)}</span>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default RigTrailers;
